import { Injectable, Injector, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MarqueeTilesConfig, SportsUserConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { GeoLocationStatus, MarqueeTile } from '@frontend/sports/types/components/content';
import { ClientConfigService, GeolocationService, OnAppInit } from '@frontend/vanilla/core';
import { ReplaySubject, combineLatest, concatMap, distinctUntilChanged, first, from, map, skip, startWith, switchMap, tap } from 'rxjs';

import { UserEvents } from '../model/user.models';

@Injectable({
    providedIn: 'root',
})
export class SportsClientConfigRefreshService implements OnAppInit {
    private geolocationService = inject(GeolocationService);
    private injector = inject(Injector);
    private dispatcher = inject(DispatcherService);
    private sportsUserConfig = inject(SportsUserConfig);
    private marqueeTilesConfig = inject(MarqueeTilesConfig);
    private router = inject(Router);

    private readonly BET_PLACE_SUCCESS = 'BET_PLACE_SUCCESS';
    private _locationDependantDataNeeded$ = new ReplaySubject<void>(1);

    readonly locationDependantDataNeeded$ = this._locationDependantDataNeeded$.asObservable();

    onAppInit(): void {
        const clientConfig = this.injector.get(ClientConfigService);

        const firstNavigationFinished$ = this.router.events.pipe(first((e) => e instanceof NavigationEnd));

        this.geolocationService.whenReady
            .pipe(
                first(),
                concatMap(() =>
                    combineLatest([this.geolocationService.positionChanges, firstNavigationFinished$, this.marqueeTilesConfig.whenReady]).pipe(
                        map(([location]) => location.mappedLocation?.locationId),
                        distinctUntilChanged(),
                        switchMap(() => from(clientConfig.reload([MarqueeTilesConfig]))),
                        tap(() => {
                            this.dispatcher.dispatch(UserEvents.ClientConfigRefreshed);
                        }),
                        map(() => this.marqueeTilesConfig.tiles.some((t: MarqueeTile) => t.geoLocationStatus === GeoLocationStatus.Restricted)),
                        startWith(false),
                        distinctUntilChanged(),
                        skip(1), // we don't care about the first value which is emitted from startWith - it's used just for distinctUntilChanged
                    ),
                ),
            )
            .subscribe(() => {
                this._locationDependantDataNeeded$.next();
            });

        this.dispatcher.on(this.BET_PLACE_SUCCESS).subscribe(() => {
            if (this.sportsUserConfig.isEligibleForNewCustomerOffer) {
                clientConfig.reload([SportsUserConfig]);
            }
        });
    }
}
